import { useNavigate } from '@tanstack/react-router';
import { BookOpen, Search, Truck } from 'lucide-react';
import { useId, useState, type KeyboardEvent, type ReactNode } from 'react';

import { explicarFalha } from '@/lib/pedido';
import { combinaComTermo } from '@/lib/texto';
import { modulosVisiveis } from '@/modulos/acesso/consultas';
import { CAMPOS_DA_BUSCA } from '@/modulos/equipamentos/busca';
import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';
import { useDocumentos } from '@/modulos/tecnico/consultas';

const ID_AVISO_DA_BUSCA = 'busca-fora-do-perfil';
const ID_CAMPOS_DA_BUSCA = 'busca-campos';

/** Quantos documentos o menu mostra; o resto fica a um Enter na biblioteca. */
const DOCUMENTOS_NO_MENU = 5;
/** Com uma letra só, a biblioteca inteira "combina": o menu espera a segunda. */
const LETRAS_PARA_ABRIR = 2;

const AVISO_DA_BUSCA_FORA_DO_PERFIL =
  'A busca de equipamento vai para a lista de equipamentos, que está fora do seu perfil na matriz de permissões. Peça a quem administra o sistema para incluir a leitura de equipamentos no seu papel.';

interface Opcao {
  readonly chave: string;
  readonly escolher: () => void;
}

/**
 * A BUSCA DA BARRA SUPERIOR (p.35): equipamento e biblioteca técnica.
 *
 * Digitar abre o menu de resultados COLADO AO CAMPO, a 8 px (p.30), em duas
 * seções: Equipamentos — a lista da frota com o termo, o mesmo `?termo=` que
 * o filtro da página escreve — e Biblioteca técnica, os documentos cujo título
 * combina, cada um abrindo o painel dele em `/tecnico?documento=`.
 *
 * O Enter sem escolher continua indo para a frota, como antes: quem já usava
 * a barra não descobre um comportamento novo. Setas escolhem, `Esc` fecha.
 *
 * A seção da biblioteca tem os cinco estados (p.32): carregando, vazia, sem
 * resultado, erro e sem permissão — esta última diz o que pedir, em vez de
 * sumir com a seção.
 *
 * Não é a busca global com Ctrl K: o atalho continua sendo do campo de busca
 * da página que o tem (`CampoTexto atalho="ctrl-k"`), e esta barra não o
 * disputa.
 */
export function BuscaDaBarra() {
  const navegar = useNavigate();
  const sessao = useSessaoEmMemoria();
  const [termo, setTermo] = useState('');
  const [aberto, setAberto] = useState(false);
  const [ativa, setAtiva] = useState(0);
  const base = useId();
  const idDoMenu = `${base}-menu`;

  const permissoes = sessao?.permissoes ?? null;
  const visiveis = permissoes === null ? null : modulosVisiveis(permissoes);
  const buscaForaDoPerfil = visiveis !== null && !visiveis.includes('equipamentos');
  /* Sem a sessão em memória ainda, a seção espera (carregando), e não promete
     nem nega a biblioteca. */
  const leBiblioteca = visiveis !== null && visiveis.includes('biblioteca');
  const documentos = useDocumentos({ ligada: leBiblioteca });

  const limpo = termo.trim();
  const mostrando = aberto && limpo.length >= LETRAS_PARA_ABRIR;
  const achados = (documentos.data ?? []).filter((d) => combinaComTermo([d.titulo], limpo));
  const noMenu = achados.slice(0, DOCUMENTOS_NO_MENU);

  const fechar = () => {
    setAberto(false);
    setAtiva(0);
  };
  const irParaAFrota = () => {
    fechar();
    void navegar({ to: '/equipamentos', search: limpo ? { termo: limpo } : {} });
  };
  const opcoes: readonly Opcao[] = [
    { chave: 'frota', escolher: irParaAFrota },
    ...noMenu.map((d) => ({
      chave: d.id,
      escolher: () => {
        fechar();
        setTermo('');
        void navegar({ to: '/tecnico', search: { documento: d.id } });
      },
    })),
  ];
  const idDa = (i: number) => `${base}-opcao-${String(i)}`;

  const aoTeclar = (evento: KeyboardEvent<HTMLInputElement>) => {
    if (evento.key === 'Escape' && mostrando) {
      evento.preventDefault();
      fechar();
      return;
    }
    if (evento.key !== 'ArrowDown' && evento.key !== 'ArrowUp') return;
    evento.preventDefault();
    setAberto(true);
    if (!mostrando) return;
    const passo = evento.key === 'ArrowDown' ? 1 : -1;
    setAtiva((atual) => (atual + passo + opcoes.length) % opcoes.length);
  };

  const camposDaBusca = `Procura equipamento por ${CAMPOS_DA_BUSCA.toLocaleLowerCase('pt-BR')}, e documento da biblioteca técnica pelo título.`;

  return (
    <form
      role="search"
      className="relative w-72 min-w-40 max-[1024px]:w-56 max-[768px]:w-auto max-[768px]:flex-1"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (mostrando) (opcoes[ativa] ?? opcoes[0])?.escolher();
        else irParaAFrota();
      }}
    >
      <Search aria-hidden="true" className="pointer-events-none absolute top-1/2 left-3 size-[var(--icone-controle)] -translate-y-1/2" />
      <input
        type="text"
        role="combobox"
        value={termo}
        aria-label="Buscar equipamento ou documento"
        aria-autocomplete="list"
        aria-expanded={mostrando}
        aria-controls={idDoMenu}
        {...(mostrando ? { 'aria-activedescendant': idDa(ativa) } : {})}
        placeholder="Buscar equipamento ou manual"
        className="controle h-[var(--controle-p)] w-full rounded-pill pl-9"
        onChange={(evento) => {
          setTermo(evento.target.value);
          setAberto(true);
          setAtiva(0);
        }}
        onFocus={() => {
          setAberto(true);
        }}
        onBlur={fechar}
        onKeyDown={aoTeclar}
        title={buscaForaDoPerfil ? AVISO_DA_BUSCA_FORA_DO_PERFIL : camposDaBusca}
        aria-describedby={buscaForaDoPerfil ? `${ID_CAMPOS_DA_BUSCA} ${ID_AVISO_DA_BUSCA}` : ID_CAMPOS_DA_BUSCA}
      />
      <span id={ID_CAMPOS_DA_BUSCA} className="sr-only">
        {camposDaBusca}
      </span>
      {buscaForaDoPerfil ? (
        <span id={ID_AVISO_DA_BUSCA} className="sr-only">
          {AVISO_DA_BUSCA_FORA_DO_PERFIL}
        </span>
      ) : null}

      {/* O menu colado ao campo, a 8 px (p.30), alinhado pela direita para não
          sair da tela: a busca mora na ponta direita da barra. */}
      <div
        id={idDoMenu}
        role="listbox"
        aria-label={`Resultados para ${limpo}`}
        hidden={!mostrando}
        /* O clique numa opção não pode tirar o foco do campo antes de valer. */
        onMouseDown={(evento) => {
          evento.preventDefault();
        }}
        className="anima-menu absolute top-[calc(100%+var(--flutuante-distancia))] right-0 z-[var(--z-dica)] w-[var(--popover-max)] max-w-[calc(100vw-var(--esp-8))] rounded-card border border-borda-sutil bg-flutuante p-1 shadow-elevado"
      >
        <TituloDaSecao id={`${base}-equipamentos`}>Equipamentos</TituloDaSecao>
        <div role="group" aria-labelledby={`${base}-equipamentos`}>
          <Item id={idDa(0)} ativa={ativa === 0} icone={<Truck aria-hidden="true" className="size-[var(--icone-controle)] shrink-0" />} aoEscolher={irParaAFrota}>
            <span className="block truncate">
              Procurar <span className="font-semibold">{limpo}</span> na frota
            </span>
            {buscaForaDoPerfil ? <span className="txt-linha2 block text-tinta-secundaria">Fora do seu perfil: a lista pode recusar.</span> : null}
          </Item>
        </div>

        <TituloDaSecao id={`${base}-biblioteca`}>Biblioteca técnica</TituloDaSecao>
        <div role="group" aria-labelledby={`${base}-biblioteca`}>
          {visiveis !== null && !leBiblioteca ? (
            <Nota>A biblioteca técnica está fora do seu perfil. Peça a quem administra o sistema a leitura de documentos técnicos.</Nota>
          ) : visiveis === null || documentos.isPending ? (
            <div aria-busy="true" className="flex flex-col gap-2 px-3 py-2">
              <span className="sr-only">Procurando na biblioteca</span>
              <span aria-hidden="true" className="esqueleto block h-4 w-48 rounded-badge" />
              <span aria-hidden="true" className="esqueleto block h-4 w-32 rounded-badge" />
            </div>
          ) : documentos.isError ? (
            <Nota>{`Não foi possível procurar na biblioteca. ${explicarFalha(documentos.error).comoResolver}`}</Nota>
          ) : (documentos.data ?? []).length === 0 ? (
            <Nota>A biblioteca ainda não tem documento. Cadastre o primeiro manual em Biblioteca técnica.</Nota>
          ) : noMenu.length === 0 ? (
            <Nota>{`Nenhum documento com “${limpo}” no título. Confira a grafia ou procure por outra palavra.`}</Nota>
          ) : (
            noMenu.map((d, i) => (
              <Item
                key={d.id}
                id={idDa(i + 1)}
                ativa={ativa === i + 1}
                icone={<BookOpen aria-hidden="true" className="size-[var(--icone-controle)] shrink-0" />}
                aoEscolher={() => {
                  opcoes[i + 1]?.escolher();
                }}
              >
                <span className="block truncate" title={d.titulo}>
                  {d.titulo}
                </span>
                {d.revisao ? <span className="txt-linha2 block text-tinta-secundaria">{`rev. ${d.revisao}`}</span> : null}
              </Item>
            ))
          )}
          {achados.length > noMenu.length ? (
            <Nota>{`E mais ${String(achados.length - noMenu.length)} na biblioteca.`}</Nota>
          ) : null}
        </div>
      </div>
    </form>
  );
}

function TituloDaSecao({ id, children }: { readonly id: string; readonly children: ReactNode }) {
  return (
    <p id={id} role="presentation" className="txt-legenda px-3 pt-2 pb-1 font-semibold text-tinta-secundaria">
      {children}
    </p>
  );
}

function Nota({ children }: { readonly children: ReactNode }) {
  return <p className="txt-apoio px-3 py-2 text-tinta-secundaria">{children}</p>;
}

function Item({
  id,
  ativa,
  icone,
  aoEscolher,
  children,
}: {
  readonly id: string;
  readonly ativa: boolean;
  readonly icone: ReactNode;
  readonly aoEscolher: () => void;
  readonly children: ReactNode;
}) {
  return (
    <div
      id={id}
      role="option"
      aria-selected={ativa}
      onClick={aoEscolher}
      className={`txt-corpo flex w-full cursor-pointer items-center gap-2 rounded-controle px-3 py-2 text-left text-tinta hover:bg-rebaixada ${ativa ? 'bg-rebaixada' : ''}`}
    >
      {icone}
      <span className="min-w-0 flex-1">{children}</span>
    </div>
  );
}
